import React, { useContext } from 'react';
import PropTypes from 'prop-types';
import { Box, Typography, IconButton, Tooltip } from '@mui/material';
import { MoreVert as MoreVertIcon, Lock as LockIcon } from '@mui/icons-material';
import { AuthContext } from '../../../contexts/AuthContext';
import sectionColorService from '../../../services/sectionColorService';
import { getStatusIndicator, TASK_STATUS } from '../../../utils/taskStatusIndicator';

/**
 * Build tooltip text for validation status indicators
 */
const getValidationTooltip = (status, validationInfo) => {
  if (!validationInfo) return '';

  const intVal = validationInfo.internal_validation;
  const extVal = validationInfo.external_validation;

  const formatEntry = (label, val) => {
    if (!val) return '';
    const by = val.validated_by?.name || 'Unknown';
    const date = val.validated_at ? new Date(val.validated_at).toLocaleString('en-US', {
      month: 'short', day: 'numeric', year: 'numeric', hour: 'numeric', minute: '2-digit', hour12: true
    }) : '';
    const statusText = val.is_stale ? 'Stale' : val.status === 'approved' ? 'Approved' : 'Changes requested';
    let text = `${label}: ${statusText} by ${by}`;
    if (date) text += ` (${date})`;
    if (val.comment) text += `\n"${val.comment}"`;
    return text;
  };

  switch (status) {
    case TASK_STATUS.VALIDATED_INTERNAL:
      return formatEntry('Internal', intVal);
    case TASK_STATUS.VALIDATED_EXTERNAL:
      return formatEntry('External', extVal);
    case TASK_STATUS.VALIDATED_BOTH: {
      const parts = [formatEntry('Internal', intVal), formatEntry('External', extVal)].filter(Boolean);
      return parts.join('\n');
    }
    case TASK_STATUS.VALIDATION_STALE: {
      const parts = [];
      if (intVal?.is_stale) parts.push(formatEntry('Internal (stale)', intVal));
      if (extVal?.is_stale) parts.push(formatEntry('External (stale)', extVal));
      if (parts.length === 0) parts.push('Content changed after validation. Re-review needed.');
      return parts.join('\n');
    }
    case TASK_STATUS.CHANGES_REQUESTED: {
      const parts = [];
      if (intVal?.status === 'changes_requested') parts.push(formatEntry('Internal', intVal));
      if (extVal?.status === 'changes_requested') parts.push(formatEntry('External', extVal));
      return parts.join('\n');
    }
    default:
      return '';
  }
};

/**
 * Helper function to format task titles with proper capitalization
 */
const formatTaskTitle = (title) => {
  if (!title) return '';

  // First, capitalize first letter and lowercase the rest
  let formatted = title.charAt(0).toUpperCase() + title.slice(1).toLowerCase();

  // Then replace known acronyms with their proper capitalization
  formatted = formatted.replace(/\bui\/ux\b/gi, 'UI/UX');
  formatted = formatted.replace(/\bci\/cd\b/gi, 'CI/CD');
  formatted = formatted.replace(/\bci cd\b/gi, 'CI/CD');
  formatted = formatted.replace(/\bapi\b/gi, 'API');
  formatted = formatted.replace(/\bprd\b/gi, 'PRD');
  return formatted;
};

/**
 * Task tile component for displaying a single task
 * @param {Object} props - Component props
 * @param {Object} props.tile - Tile data object containing task details
 * @param {Function} props.onMenuOpen - Function to open the menu
 * @param {Function} props.onClick - Function to handle click on the tile
 * @returns {React.ReactElement} Task tile component
 */
const TASK_MENU_PERMISSIONS = [
  'foundation.task.edit',
  'foundation.task.download',
  'project.validators.assign',
  'project.validators.assign.internal',
  'project.validators.assign.external',
  'foundation.task.validation.history',
  'foundation.task.validation.history.all',
  'foundation.remove.topic',
];

const TaskTile = ({ tile, onMenuOpen, onClick, validationInfo, disabled, disabledTooltip }) => {
  const { hasPermission } = useContext(AuthContext);
  const hasAnyMenuPermission = TASK_MENU_PERMISSIONS.some(p => hasPermission(p));

  // Get colors from the centralized service
  let { backgroundColor, iconColor } = sectionColorService.getTaskColors({
    task: tile,
    sectionId: tile.sectionId,
    sectionName: tile.sectionName,
    status: tile.status
  });

  const tileContent = (
    <Box
      sx={{
        height: 140,
        width: '100%',
        maxWidth: { xs: '180px', md: '170px', lg: '160px', xl: '160px' },
        display: 'flex',
        flexDirection: 'column',
        borderRadius: 0,
        overflow: 'hidden',
        position: 'relative',
        backgroundColor: disabled ? '#f0f0f0' : backgroundColor,
        cursor: disabled ? 'not-allowed' : 'pointer',
        transition: 'all 0.2s ease',
        ml: 'auto',
        mr: 'auto',
        mb: 1,
        border: 'none',
        opacity: disabled ? 0.55 : 1,
        filter: disabled ? 'grayscale(0.4)' : 'none',
        '&:hover': disabled ? {} : {
          transform: 'translateY(-3px)',
          boxShadow: '0 4px 8px rgba(0,0,0,0.05)'
        }
      }}
      onClick={() => { if (!disabled) onClick(tile); }}
    >
      {/* Lock icon for disabled tiles */}
      {disabled && (
        <Box
          sx={{
            position: 'absolute',
            top: 8,
            left: 8,
            zIndex: 3,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            width: 24,
            height: 24,
            borderRadius: '50%',
            backgroundColor: 'rgba(0,0,0,0.12)',
          }}
        >
          <LockIcon sx={{ fontSize: 14, color: '#757575' }} />
        </Box>
      )}

      {/* Status indicator */}
      {tile.status && (
        (() => {
          const statusIndicator = getStatusIndicator(tile.status);
          if (statusIndicator && statusIndicator.icon) {
            const tooltipText = getValidationTooltip(tile.status, validationInfo);
            const indicator = (
              <Box
                sx={{
                  position: 'absolute',
                  top: statusIndicator.position?.top || 10,
                  right: statusIndicator.position?.right || 10,
                  zIndex: 2
                }}
              >
                {statusIndicator.icon}
              </Box>
            );
            if (tooltipText) {
              return (
                <Tooltip
                  title={<span style={{ whiteSpace: 'pre-line' }}>{tooltipText}</span>}
                  arrow
                  placement="top"
                >
                  {indicator}
                </Tooltip>
              );
            }
            return indicator;
          }
          return null;
        })()
      )}
      
      {/* Menu button at bottom right — pushed up when approval strip visible */}
      {hasAnyMenuPermission && (
      <Tooltip title="View actions">
        <IconButton
          sx={{
            position: 'absolute',
            bottom: 2,
            right: 2,
            zIndex: 3,
            opacity: 0.7,
            color: '#475969',
            padding: '4px',
            minWidth: '28px',
            minHeight: '28px',
            '&:hover': {
              opacity: 1,
              backgroundColor: 'rgba(0, 0, 0, 0.05)'
            },
          }}
          size="small"
          onClick={(e) => {
            e.stopPropagation();
            e.preventDefault();
            if (onMenuOpen) onMenuOpen(e, tile);
          }}
        >
          <MoreVertIcon style={{ fontSize: '18px' }} />
        </IconButton>
      </Tooltip>
      )}
      
      <Box 
        sx={{ 
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
          height: '100%',
          width: '100%',
          pt: 4,
          pb: 3.5,
          px: 1.5,
          textAlign: 'center'
        }}
      >
        <Box
          sx={{
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
            mb: 2,
            height: '60px',
            width: '60px',
            borderRadius: '50%',
            backgroundColor: 'rgba(255,255,255,0.6)',
            color: iconColor
          }}
        >
          {tile.icon && React.isValidElement(tile.icon)
            ? React.cloneElement(tile.icon, { style: { fontSize: '2.2rem', color: 'inherit' } })
            : <span style={{ fontSize: '2.2rem' }}>📄</span>
          }
        </Box>
        
        <Typography
          variant="body1"
          component="h3"
          sx={{
            fontSize: '1rem',
            fontWeight: 500,
            color: '#475969',
            lineHeight: 1.3,
            mt: 1,
            // Limit to 2 lines with ellipsis
            display: '-webkit-box',
            WebkitLineClamp: 2,
            WebkitBoxOrient: 'vertical',
            overflow: 'hidden'
          }}
        >
          {formatTaskTitle(tile.title)}
        </Typography>
      </Box>

    </Box>
  );

  if (disabled && disabledTooltip) {
    return (
      <Tooltip title={disabledTooltip} arrow placement="top">
        <Box sx={{ display: 'inline-block', width: '100%' }}>
          {tileContent}
        </Box>
      </Tooltip>
    );
  }

  return tileContent;
};

TaskTile.propTypes = {
  tile: PropTypes.object.isRequired,
  onMenuOpen: PropTypes.func.isRequired,
  onClick: PropTypes.func.isRequired,
  validationInfo: PropTypes.object,
  disabled: PropTypes.bool,
  disabledTooltip: PropTypes.string
};

export default React.memo(TaskTile);